import { WebAuthTokens } from '../models/web-auth-tokens';
import { OAuth2AuthenticateOptions, OAuth2Client } from '@byteowls/capacitor-oauth2';
import { AuthResultEnum } from '../models/auth-result.enum';
import { RefreshAuthResultEnum } from '../models/refresh-auth-result.enum';
import { AndroidAuthTokens } from '../models/android-auth-tokens';
import { PlatformDetectionService } from '@libs/util/shared';
import { StorageService } from '@libs/core/storage';
import { defer, from, merge, ReplaySubject, shareReplay, startWith, Subject, switchMap } from 'rxjs';
import { StandardAuthTokens } from '../models/standard-auth-tokens';
import { User } from '../models/user';
import { Browser } from '@capacitor/browser';
import { NavController } from '@ionic/angular';

export abstract class BaseAuthService<TRawUser extends User, TUser> {
  /**
   * The window of time (in seconds) to allow the current time to deviate when validating access_token's iat and exp values.
   */
  public clockSkewInSec?: number;

  private readonly _login$ = new ReplaySubject(1);
  private readonly _logout$ = new Subject();

  readonly loginCompleted$ = this._login$.asObservable().pipe(shareReplay(1));
  readonly logoutCompleted$ = this._logout$.asObservable().pipe(shareReplay(1));

  readonly user$ = merge([this._login$, this._logout$]).pipe(
    startWith(void 0),
    switchMap(() => defer(() => from(this.getUserInfo()))),
  );

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly ACCESS_TOKEN_EXP_KEY = 'access_token_expires_at';

  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly REFRESH_TOKEN_EXP_KEY = 'refresh_token_expires_at';

  private readonly USER_INFO_KEY = 'user_info';

  protected constructor(
    private _storageService: StorageService,
    private platformService: PlatformDetectionService,
    private authOptions: OAuth2AuthenticateOptions,
    private _navController: NavController,
  ) {}

  async authenticate(): Promise<AuthResultEnum> {
    try {
      // return AuthResultEnum.SUCCESS;
      const response = await OAuth2Client.authenticate(this.authOptions);
      // console.log(response);

      const tokenResponse = response.access_token_response;
      const user = JSON.parse(window.atob(tokenResponse.access_token.split('.')[1]));

      /*
       * ATTENTION: this is a temporary fix. When the user reset his password, Azure B2C doesn't call the
       * enrich token endpoint so there will be no Companies claim in the token. To avoid that, as of now,
       * we let the user login again.
       */
      if (user.isForgotPassword) {
        return AuthResultEnum.FORGOT_PASSWORD;
      }

      const storeResult = await this.storeTokens(tokenResponse);

      if (!storeResult) {
        console.error('[AuthService] Unable to store tokens in store');
        return AuthResultEnum.CANNOT_STORE_VALUES;
      }

      await this.storeUserInfo(user);

      const afterLoginResult = await this.afterLogin();

      if (afterLoginResult == AuthResultEnum.NOT_AUTHORIZED) return afterLoginResult;

      this._login$.next(void 0);

      return AuthResultEnum.SUCCESS;
    } catch (e) {
      console.error('[AuthService] Authentication Failed', e);

      // The user cancelled the authentication process
      if (e instanceof Error && e?.message === 'USER_CANCELLED') {
        return AuthResultEnum.USER_CANCELLED;
      }

      return AuthResultEnum.ERROR;
    }
  }

  async autoLogin() {
    if (await this.isAccessTokenValid()) {
      await this.afterLogin();

      this._login$.next(void 0);
      return;
    }

    console.log('[AuthService] Autologin: access token expired. Trying to refresh it');

    if ((await this.refreshToken()) === RefreshAuthResultEnum.SUCCESS) {
      await this.afterLogin();

      this._login$.next(void 0);
    }
  }

  async refreshToken(): Promise<RefreshAuthResultEnum> {
    if (!this.isSupportedRefreshTokensPlatform()) {
      console.log(`[AuthService] Platform doesn't support refresh token`);
      return RefreshAuthResultEnum.PLATFORM_NOT_SUPPORTED;
    }

    if (!(await this.isRefreshTokenStored())) {
      console.log('[AuthService] No refresh token stored');
      return RefreshAuthResultEnum.NO_STORED;
    }

    if (await this.isRefreshTokenExpired()) {
      console.log('[AuthService] Refresh token expired');
      return RefreshAuthResultEnum.EXPIRED;
    }

    const refreshToken = await this.getRefreshToken();

    try {
      const response = await OAuth2Client.refreshToken({
        accessTokenEndpoint: this.authOptions.accessTokenEndpoint!,
        appId: this.authOptions.appId!,
        scope: this.authOptions.scope,
        refreshToken: refreshToken!,
      });

      const storeResult = await this.storeTokens(response);

      if (!storeResult) {
        console.error('[AuthService] Unable to store tokens in store');
        return RefreshAuthResultEnum.NO_STORED;
      }

      console.log('[AuthService] Access token refreshed');
      return RefreshAuthResultEnum.SUCCESS;
    } catch (e) {
      console.error('[AuthService] Refresh token error', e);
      return RefreshAuthResultEnum.ERROR;
    }
  }

  private async storeTokens(token: WebAuthTokens | AndroidAuthTokens) {
    const standardAuthToken = this.getStandardAuthToken(token);

    const results = await Promise.all([
      // Access token
      this._storageService.set(this.ACCESS_TOKEN_KEY, standardAuthToken.accessToken),
      this._storageService.set(this.ACCESS_TOKEN_EXP_KEY, standardAuthToken.accessTokenExpiresAt.toString()),

      // Refresh token
      this._storageService.set(this.REFRESH_TOKEN_KEY, standardAuthToken.refreshToken),
      this._storageService.set(this.REFRESH_TOKEN_EXP_KEY, standardAuthToken.refreshTokenExpiresAt.toString()),
    ]);

    return results.every((result) => result);
  }

  private getStandardAuthToken(token: WebAuthTokens | AndroidAuthTokens): StandardAuthTokens {
    const result = {
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
    };

    if (this.isAndroidAuthToken(token)) {
      return {
        ...result,
        accessTokenExpiresAt: token.expires_at,
        refreshTokenExpiresAt: this.expiresInToExpiresAt(token.additionalParameters.refresh_token_expires_in),
      };
    }

    return {
      ...result,
      accessTokenExpiresAt: this.expiresInToExpiresAt(token.expires_in),
      refreshTokenExpiresAt: this.expiresInToExpiresAt(token.refresh_token_expires_in),
    };
  }

  private isAndroidAuthToken(token: WebAuthTokens | AndroidAuthTokens): token is AndroidAuthTokens {
    return this.platformService.isAndroid();
  }

  private storeUserInfo(user: TRawUser) {
    return this._storageService.set(this.USER_INFO_KEY, JSON.stringify(user));
  }

  private isSupportedRefreshTokensPlatform() {
    return this.platformService.isMobile();
  }

  async getUserInfo() {
    const userString = await this._storageService.get(this.USER_INFO_KEY);

    if (!userString) {
      return null;
    }

    const rawUser = <TRawUser>JSON.parse(userString);

    return this.mapUser(rawUser);
  }

  abstract mapUser(rawUser: TRawUser): TUser;

  async afterLogin(): Promise<AuthResultEnum | void> {
    return Promise.resolve(undefined);
  }

  getAccessToken() {
    return this._storageService.get(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return this._storageService.get(this.REFRESH_TOKEN_KEY);
  }

  isAccessTokenStored() {
    return this.isTokenStored(this.ACCESS_TOKEN_KEY);
  }

  isRefreshTokenStored() {
    return this.isTokenStored(this.REFRESH_TOKEN_KEY);
  }

  private async isTokenStored(tokenKey: string) {
    const token = await this._storageService.get(tokenKey);
    return token != null;
  }

  async isAccessTokenValid() {
    return !(await this.isAccessTokenExpired());
  }

  async isAccessTokenExpired() {
    return this.isTokenExpired(this.ACCESS_TOKEN_KEY, this.ACCESS_TOKEN_EXP_KEY);
  }

  async isRefreshTokenExpired() {
    return this.isTokenExpired(this.REFRESH_TOKEN_KEY, this.REFRESH_TOKEN_EXP_KEY);
  }

  private async isTokenExpired(tokenKey: string, expiresAtKey: string) {
    const token = await this._storageService.get(tokenKey);

    if (token) {
      const expiresAt = await this.getTokenExpiration(expiresAtKey);

      if (expiresAt && expiresAt < Date.now() - this.getClockSkewInMsec()) {
        return true;
      }

      return false;
    }

    return true;
  }

  private async getTokenExpiration(tokenKey: string) {
    const expiresAt = await this._storageService.get(tokenKey);

    if (!expiresAt) {
      return null;
    }

    return parseInt(expiresAt, 10);
  }

  private expiresInToExpiresAt(expiresIn: number) {
    return Date.now() + expiresIn * 1000;
  }

  async logout() {
    await this.clearStoredTokens();

    if (!this.authOptions.logoutUrl) {
      return;
    }

    const redirectUrl = this.platformService.isWebBrowser()
      ? this.authOptions.web?.redirectUrl
      : this.platformService.isAndroid()
        ? this.authOptions.android?.redirectUrl
        : this.platformService.isIos()
          ? this.authOptions.ios?.redirectUrl
          : this.authOptions.redirectUrl;

    if (!redirectUrl) {
      return;
    }

    const url = new URL(this.authOptions.logoutUrl);
    url.searchParams.append('post_logout_redirect_uri', redirectUrl);

    await this.logoutOnBrowser(url.toString());

    try {
      // This will throw an exception since the method is not implemented by the library
      await OAuth2Client.logout(this.authOptions);
    } catch {
      /* empty */
    } finally {
      this._logout$.next(void 0);
    }
  }

  private async logoutOnBrowser(logoutUrl: string) {
    if (this.platformService.isWebBrowser()) {
      window.location.href = logoutUrl;
      return;
    }

    let notifyPageLoaded: () => void;
    const pageLoaded = new Promise<void>((resolve) => {
      notifyPageLoaded = resolve;
    });

    await Browser.addListener('browserPageLoaded', () => {
      notifyPageLoaded();
    });
    await Promise.all([this._navController.navigateRoot('/login', { replaceUrl: true }), Browser.open({ url: logoutUrl })]);

    const browserPageLoadedTimeout = new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 3000);
    });

    await Promise.race([pageLoaded, browserPageLoadedTimeout]);

    try {
      // This will throw an exception in Android since the method is not implement
      await Browser.close();
    } catch {
      /* empty */
    } finally {
      await Browser.removeAllListeners();
    }
  }

  protected async clearStoredTokens() {
    await this._storageService.clear();
  }

  private getClockSkewInMsec(defaultSkewMsc = 600_000) {
    if (!this.clockSkewInSec) {
      return defaultSkewMsc;
    }
    return this.clockSkewInSec * 1000;
  }
}
